import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus,
  Edit,
  Trash2,
  X,
  AlertCircle,
  Filter,
  Droplets,
  Flame,
  Layers,
  GripVertical,
  Package,
} from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import type { FilterDefinition, FilterType } from '@/types'

const filterSchema = z.object({
  value: z.string().min(1, 'Value is required'),
  label: z.string().min(1, 'Label is required'),
})

type FilterFormData = z.infer<typeof filterSchema>

// Order matches filterTypeLabels in useFilters.ts - this is the canonical order
const filterTypes: { type: FilterType; label: string; icon: typeof Filter; color: string }[] = [
  { type: 'water_tightness', label: 'Water Tightness', icon: Droplets, color: 'bg-blue-100 text-blue-700' },
  { type: 'fire_class', label: 'Fire Class', icon: Flame, color: 'bg-red-100 text-red-700' },
  { type: 'material', label: 'Material', icon: Layers, color: 'bg-gray-100 text-gray-700' },
  { type: 'product', label: 'Products', icon: Package, color: 'bg-purple-100 text-purple-700' },
]

function SortableItem({
  filter,
  onEdit,
  onDelete,
  typeColor,
}: {
  filter: FilterDefinition
  onEdit: () => void
  onDelete: () => void
  typeColor: string
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: filter.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 p-3 bg-white border border-secondary-200 rounded-lg',
        isDragging && 'shadow-lg opacity-90 z-10'
      )}
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 text-secondary-400 hover:text-secondary-600 cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span className={cn('badge text-xs', typeColor)}>{filter.value.toUpperCase()}</span>
      <span className="flex-1 text-sm text-secondary-700">{filter.label}</span>
      <div className="flex items-center gap-1">
        <button
          onClick={onEdit}
          className="p-1.5 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
        >
          <Edit className="w-4 h-4" />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

export function FilterDefinitionsPage() {
  const [selectedType, setSelectedType] = useState<FilterType>('water_tightness')
  const [editingFilter, setEditingFilter] = useState<FilterDefinition | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const queryClient = useQueryClient()

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const { data: allFilters, isLoading } = useQuery({
    queryKey: ['admin-filter-definitions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('filter_definitions')
        .select('*')
        .order('sort_order')
      if (error) throw error
      return data as FilterDefinition[]
    },
  })

  // Group filters by type
  const filtersByType: Partial<Record<FilterType, FilterDefinition[]>> = allFilters?.reduce(
    (acc: Partial<Record<FilterType, FilterDefinition[]>>, f: FilterDefinition) => {
      if (!acc[f.type]) acc[f.type] = []
      acc[f.type]!.push(f)
      return acc
    },
    {}
  ) || {}

  const currentFilters: FilterDefinition[] = filtersByType[selectedType] ?? []
  const currentTypeInfo = filterTypes.find((t) => t.type === selectedType)!

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FilterFormData>({
    resolver: zodResolver(filterSchema),
    defaultValues: { value: '', label: '' },
  })

  const openModal = (filter?: FilterDefinition) => {
    if (filter) {
      setEditingFilter(filter)
      reset({ value: filter.value, label: filter.label })
    } else {
      setEditingFilter(null)
      reset({ value: '', label: '' })
    }
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingFilter(null)
    reset({ value: '', label: '' })
  }

  const saveMutation = useMutation({
    mutationFn: async (data: FilterFormData) => {
      if (editingFilter) {
        const { error } = await supabase
          .from('filter_definitions')
          .update({ value: data.value, label: data.label })
          .eq('id', editingFilter.id)
        if (error) throw error
      } else {
        const maxSortOrder = currentFilters.reduce((max: number, f: FilterDefinition) => Math.max(max, f.sort_order), -1)
        const { error } = await supabase.from('filter_definitions').insert({
          type: selectedType,
          value: data.value,
          label: data.label,
          sort_order: maxSortOrder + 1,
        })
        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-filter-definitions'] })
      queryClient.invalidateQueries({ queryKey: ['filter-definitions'] })
      closeModal()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('filter_definitions').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-filter-definitions'] })
      queryClient.invalidateQueries({ queryKey: ['filter-definitions'] })
    },
  })

  const reorderMutation = useMutation({
    mutationFn: async (filters: FilterDefinition[]) => {
      const updates = filters.map((f, index) => ({
        id: f.id,
        type: f.type,
        value: f.value,
        label: f.label,
        sort_order: index,
      }))
      const { error } = await supabase.from('filter_definitions').upsert(updates)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-filter-definitions'] })
      queryClient.invalidateQueries({ queryKey: ['filter-definitions'] })
    },
  })

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = currentFilters.findIndex((f) => f.id === active.id)
    const newIndex = currentFilters.findIndex((f) => f.id === over.id)
    const newOrder = arrayMove(currentFilters, oldIndex, newIndex)
    reorderMutation.mutate(newOrder)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Filter Definitions</h1>
          <p className="text-secondary-600">Manage product filter types and values</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          Add Value
        </button>
      </div>

      {/* Filter Type Tabs */}
      <div className="flex gap-2">
        {filterTypes.map((ft) => {
          const Icon = ft.icon
          const count = filtersByType[ft.type]?.length || 0
          return (
            <button
              key={ft.type}
              onClick={() => setSelectedType(ft.type)}
              className={cn(
                'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
                selectedType === ft.type
                  ? 'bg-primary-600 text-white'
                  : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
              )}
            >
              <Icon className="w-4 h-4" />
              {ft.label}
              <span
                className={cn(
                  'px-1.5 py-0.5 rounded text-xs',
                  selectedType === ft.type ? 'bg-primary-500' : 'bg-secondary-200'
                )}
              >
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Filter Values List */}
      <div className="card">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          </div>
        ) : currentFilters.length > 0 ? (
          <div className="p-4">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={currentFilters.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {currentFilters.map((filter) => (
                    <SortableItem
                      key={filter.id}
                      filter={filter}
                      typeColor={currentTypeInfo.color}
                      onEdit={() => openModal(filter)}
                      onDelete={() => {
                        if (confirm(`Delete "${filter.label}"?`)) {
                          deleteMutation.mutate(filter.id)
                        }
                      }}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            <p className="text-xs text-secondary-400 mt-4">
              Drag items to reorder. This order will be used across the site.
            </p>
          </div>
        ) : (
          <div className="p-8 text-center">
            <Filter className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
            <p className="text-secondary-500">No values defined for {currentTypeInfo.label}</p>
            <button onClick={() => openModal()} className="btn-outline mt-4">
              <Plus className="w-4 h-4 mr-2" />
              Add First Value
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-4 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-md bg-white rounded-2xl shadow-2xl z-50 flex flex-col max-h-[90vh]"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-100">
                <div className="flex items-center gap-3">
                  <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', currentTypeInfo.color)}>
                    <currentTypeInfo.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-secondary-900">
                      {editingFilter ? 'Edit Value' : 'New Value'}
                    </h2>
                    <p className="text-sm text-secondary-500">{currentTypeInfo.label}</p>
                  </div>
                </div>
                <button onClick={closeModal} className="p-2 hover:bg-secondary-100 rounded-xl transition-colors">
                  <X className="w-5 h-5 text-secondary-500" />
                </button>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="flex-1 overflow-y-auto">
                <div className="p-6 space-y-5">
                  {/* Value Field */}
                  <div className="form-group">
                    <label className="label">
                      Value <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register('value')}
                      placeholder="e.g. steel, watertight, a60"
                      className={cn('input', errors.value && 'input-error')}
                    />
                    <p className="text-xs text-secondary-400 mt-1">
                      Internal value stored in database (lowercase recommended)
                    </p>
                    {errors.value && (
                      <p className="error-text">
                        <AlertCircle className="w-4 h-4" />
                        {errors.value.message}
                      </p>
                    )}
                  </div>

                  {/* Label Field */}
                  <div className="form-group">
                    <label className="label">
                      Display Label <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register('label')}
                      placeholder="e.g. Steel, Watertight, A60 Fire Rated"
                      className={cn('input', errors.label && 'input-error')}
                    />
                    <p className="text-xs text-secondary-400 mt-1">Label shown to users in filters</p>
                    {errors.label && (
                      <p className="error-text">
                        <AlertCircle className="w-4 h-4" />
                        {errors.label.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-secondary-100 bg-secondary-50">
                  <button type="button" onClick={closeModal} className="btn-outline">
                    Cancel
                  </button>
                  <button type="submit" disabled={saveMutation.isPending} className="btn-primary min-w-[120px]">
                    {saveMutation.isPending ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : editingFilter ? (
                      'Save Changes'
                    ) : (
                      'Add Value'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
